import { type ReactNode } from 'react'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import {
  clientTabTableShadow,
  clientTabTdClass,
  clientTabThClass,
} from '@/components/clients/tabs/ClientTabSection'
import { cn } from '@/lib/utils'
import type { ApiList } from '@/types'

interface Column<T> {
  header: string
  cell: (row: T, rowIndex: number) => ReactNode
  className?: string
  cellClassName?: string
}

interface Props<T> {
  columns: Column<T>[]
  data: ApiList<T> | undefined
  isLoading: boolean
  isError: boolean
  emptyMessage: string
  errorMessage: string
  page: number
  pageSize: number
  onPageChange: (p: number) => void
  onPageSizeChange: (s: number) => void
  onRowClick?: (row: T) => void
  isRowClickable?: (row: T) => boolean
  rowKey: (row: T) => string
  /** When true, omit the nested table chrome (rare). */
  embedded?: boolean
  /** Match the compact headers and hover treatment of the client history tables. */
  clientHistoryStyle?: boolean
}

// Read-only paginated table for client-detail tabs (Figma 2889:57355 chrome).
export function PaginatedTabTable<T>({
  columns,
  data,
  isLoading,
  isError,
  emptyMessage,
  errorMessage,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  isRowClickable,
  rowKey,
  embedded = false,
  clientHistoryStyle = false,
}: Props<T>) {
  const rows = data?.data ?? []
  const span = columns.length

  const table = (
    <>
      <div className='overflow-x-auto'>
        <table className='w-full caption-bottom text-sm'>
          <TableHeader>
            <TableRow className='border-0'>
              {columns.map(c => (
                <TableHead
                  key={c.header}
                  className={cn(
                    clientTabThClass,
                    clientHistoryStyle &&
                      'text-[10px] font-medium leading-[14px]',
                    c.className
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i} className='border-0'>
                  <TableCell colSpan={span} className={clientTabTdClass}>
                    <Skeleton className='h-6 w-full' />
                  </TableCell>
                </TableRow>
              ))}

            {isError && !isLoading && (
              <TableRow className='border-0'>
                <TableCell
                  colSpan={span}
                  className={cn(
                    clientTabTdClass,
                    'py-10 text-center text-destructive'
                  )}
                >
                  {errorMessage}
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <TableRow className='border-0'>
                <TableCell
                  colSpan={span}
                  className={cn(
                    clientTabTdClass,
                    'py-10 text-center text-muted-foreground'
                  )}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map((row, rowIndex) => {
                const rowIsClickable =
                  !!onRowClick && (isRowClickable?.(row) ?? true)

                return (
                  <TableRow
                    key={rowKey(row)}
                    className={cn(
                      'group relative border-0',
                      rowIsClickable && 'cursor-pointer'
                    )}
                    onClick={rowIsClickable ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((c, idx) => (
                      <TableCell
                        key={c.header}
                        className={cn(
                          clientTabTdClass,
                          c.className,
                          c.cellClassName,
                          idx === 0 &&
                            '[&>div]:leading-[1.4] [&>div]:text-[#171a20] [&>div]:group-hover:text-[#ff5a1f]',
                          idx === 0 &&
                            (clientHistoryStyle
                              ? '[&>div]:font-medium'
                              : '[&>div]:font-semibold')
                        )}
                      >
                        {idx === 0 && !clientHistoryStyle && (
                          <span className='absolute inset-y-0 left-0 hidden w-1 bg-[#ff5a1f] group-hover:block' />
                        )}
                        {c.cell(row, rowIndex)}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
          </TableBody>
        </table>
      </div>

      <ListPagination
        className='border-t border-[#e7e9ef]'
        metadata={data?.metadata}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showShowingRange={false}
      />
    </>
  )

  if (embedded) {
    return <div className='overflow-hidden'>{table}</div>
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
        clientTabTableShadow
      )}
    >
      {table}
    </div>
  )
}
